// Settings › Rated orders — every field of settings/ratedOrders (see RATED_ORDERS_PLAN.md §3.2).
// Writes with set({merge:true}); bookingStatusChanged caches the doc for 5 minutes,
// the Rated Orders page reads pendingSlaHours once per visit.
import Switch from 'rc-switch';
import 'rc-switch/assets/index.css';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Input, InputGroup, InputGroupAddon, InputGroupText } from 'reactstrap';
import { firestore } from '../../../helpers/Firebase';
import { createNotification } from '../../../helpers/Utils';
import { LangPair, SettingList, SettingRow, SettingsCard, StatusPill } from './SettingsLayout';
import { APOLOGY_DEFAULTS } from '../appointments/components/apologyPush';

// Keep in sync with RATED_ORDERS_SETTINGS_DEFAULTS in functions/ratedOrders/settings.js
const DEFAULTS = {
  active: true,
  lowRatingThreshold: 3,
  customerApologyPush: false,
  apologyCopy: { en: '', ar: '' },
  pendingSlaHours: 24,
};
// Built-in push text used while the copy fields are empty (single source: appointments/components/apologyPush.js)
const APOLOGY_PLACEHOLDER = { en: APOLOGY_DEFAULTS.en.body, ar: APOLOGY_DEFAULTS.ar.body };

const RatedOrdersSettings = () => {
  const [s, setS] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    firestore().collection('settings').doc('ratedOrders').get().then((snap) => {
      const d = snap.exists ? snap.data() : {};
      const merged = { ...DEFAULTS, ...d, apologyCopy: { ...DEFAULTS.apologyCopy, ...(d.apologyCopy || {}) } };
      setS(merged);
      setUpdatedAt(d.updatedAt || null);
    }).catch((e) => createNotification('error', (e && e.message) || 'Could not load settings', 'Rated orders'))
      .finally(() => setLoading(false));
  }, []);

  const set = useCallback((k, v) => { setS((prev) => ({ ...prev, [k]: v })); setDirty(true); }, []);
  const setCopy = useCallback((lang, v) => { setS((prev) => ({ ...prev, apologyCopy: { ...prev.apologyCopy, [lang]: v } })); setDirty(true); }, []);

  const threshold = Number(s.lowRatingThreshold);
  const thresholdInvalid = !Number.isFinite(threshold) || threshold < 1 || threshold > 5;
  const invalid = thresholdInvalid;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        active: !!s.active,
        lowRatingThreshold: threshold,
        // Support email is fixed server-side (always on, to Support@chillapp.ae) since 2026-10-14; drop the legacy keys.
        notifySupportEmail: firestore.FieldValue.delete(),
        supportEmails: firestore.FieldValue.delete(),
        customerApologyPush: !!s.customerApologyPush,
        apologyTestCustomerIds: firestore.FieldValue.delete(), // test-customer allow-list retired 2026-10-14 (feature live)
        apologyCopy: { en: String(s.apologyCopy?.en || '').trim(), ar: String(s.apologyCopy?.ar || '').trim() },
        pendingSlaHours: Math.max(1, Number(s.pendingSlaHours) || DEFAULTS.pendingSlaHours),
        updatedAt: Date.now(),
      };
      await firestore().collection('settings').doc('ratedOrders').set(payload, { merge: true });
      setUpdatedAt(payload.updatedAt);
      setDirty(false);
      createNotification('success', 'Rated orders settings saved. Detection picks them up within 5 minutes.', 'Settings');
    } catch (e) {
      createNotification('error', (e && e.message) || 'Save failed', 'Settings');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="loading" />;

  const footer = (
    <>
      <span className="st-foot-note">{updatedAt ? `Last saved ${new Date(Number(updatedAt)).toLocaleString()}` : 'Never saved'}{dirty ? ' · unsaved changes' : ''}</span>
      <Button color="primary" disabled={saving || !dirty || invalid} onClick={save}>{saving ? 'Saving…' : 'Save changes'}</Button>
    </>
  );

  return (
    <SettingsCard
      title="Rated orders"
      description="Low customer ratings become complaints in the Rated Orders queue, with an alert to support. Ratings themselves are never changed."
      status={<StatusPill on={!!s.active} offLabel="Detection off" onLabel="Detecting" />}
      footer={footer}
    >
      <div className="st-section">
        <div className="st-section-title">Detection</div>
        <div className="st-switch-row">
          <div className="st-switch-text">
            <b>Detection {s.active ? 'is on' : 'is off'}</b>
            <span>Master switch. While off, new ratings are not flagged and nobody is alerted. The queue page still shows existing complaints.</span>
          </div>
          <Switch className="custom-switch custom-switch-primary" checked={!!s.active} onChange={(v) => set('active', v)} />
        </div>
        <SettingList>
          <SettingRow label="Low-rating threshold" help="A rating at or below this average becomes a complaint. The app forces a reason only at 2 or below, so between 2.1 and 3 the comment is often empty.">
            <InputGroup>
              <Input type="number" step="0.1" min="1" max="5" value={s.lowRatingThreshold == null ? '' : s.lowRatingThreshold} onChange={(e) => set('lowRatingThreshold', e.target.value)} />
              <InputGroupAddon addonType="append"><InputGroupText>stars</InputGroupText></InputGroupAddon>
            </InputGroup>
            {thresholdInvalid ? <div className="st-inline-error">Must be between 1 and 5.</div> : null}
          </SettingRow>
          <SettingRow label="Pending SLA" help="Complaints still Pending after this long are highlighted in the queue and counted in the “Pending over” tile.">
            <InputGroup>
              <Input type="number" step="1" min="1" value={s.pendingSlaHours == null ? '' : s.pendingSlaHours} onChange={(e) => set('pendingSlaHours', e.target.value)} />
              <InputGroupAddon addonType="append"><InputGroupText>hours</InputGroupText></InputGroupAddon>
            </InputGroup>
          </SettingRow>
        </SettingList>
      </div>

      <div className="st-section">
        <div className="st-section-title">Customer apology push</div>
        <SettingList>
          <SettingRow label={`Push “We're sorry” to the customer`} help="Sent to the customer once, when support first moves the complaint to In Progress (not at rating time). Applies to all customers while on.">
            <Switch className="custom-switch custom-switch-primary" checked={!!s.customerApologyPush} onChange={(v) => set('customerApologyPush', v)} />
          </SettingRow>
          <SettingRow wide label="Message" help="Leave empty to use the built-in text. {orderNumber} is replaced with the order number.">
            <LangPair
              left={<Input type="textarea" rows={3} placeholder={APOLOGY_PLACEHOLDER.en} value={s.apologyCopy?.en || ''} onChange={(e) => setCopy('en', e.target.value)} />}
              right={<Input type="textarea" rows={3} dir="rtl" placeholder={APOLOGY_PLACEHOLDER.ar} value={s.apologyCopy?.ar || ''} onChange={(e) => setCopy('ar', e.target.value)} />}
            />
          </SettingRow>
        </SettingList>
      </div>
    </SettingsCard>
  );
};

export default RatedOrdersSettings;
